import React, {useState, useEffect, useCallback} from 'react';
import {VisualSubcategoryLink} from './VisualSubcategoryLink';
import visualFilterMapping from 'constants/visualFilterMapping';
import {Carousel} from '@customink/pigment-react';
import PropTypes from 'prop-types';
import rollbar from 'rollbar';
import useBreakpoint from '../hooks/useBreakpoint';

const VisualFilters = (props) => {
  const {subcategoryNavigation, categoryName, isMobile} = props;
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [slides, setSlides] = useState([]);
  const [currentSlideIdx, setCurrentSlideIdx] = useState(null);
  const [slider, setSlider] = useState(null);
  const isMobileView = useBreakpoint() === 'sm' || useBreakpoint() === 'xs';
  const slidesPerViewDesktop = 7;
  const slidesPerViewMobile = 3;
  const slidesPerViewTablet = 4;
  const imagePath =
    process.env.NODE_ENV === 'development' ? '/products' : 'https://';
  const parentCategoryName = subcategoryNavigation?.parent_category?.name;

  const fireMetricsEvent = (category, action) => {
    CustomInk.Metrics.fireEvent(
      'plp subcat nav',
      category.toLowerCase(),
      action.toLowerCase()
    );

    window.dataLayer.push({
      event: 'catalog_filter',
      filter_type: 'cross-subcategory-navigation',
      filter_value: category.toLowerCase(),
      filter_action: action.toLowerCase()
    });
  };

  const onCreated = (_slider) => {
    setSlider(_slider);
  };

  const getCurrentSlideIdxOffset = (breakpoint) => {
    if (
      breakpoint === 'sm' ||
      breakpoint === 'xs' ||
      breakpoint === 'md' ||
      breakpoint === 'lg'
    ) {
      return 1;
    } else {
      return 3;
    }
  };

  const currentSlideIdxOffset = getCurrentSlideIdxOffset(useBreakpoint());

  const slidesPerView = () => {
    const breakpoint = useBreakpoint();
    if (breakpoint === 'xs' || breakpoint === 'sm') {
      return slidesPerViewMobile;
    } else if (breakpoint === 'md' || breakpoint === 'lg') {
      return slidesPerViewTablet;
    }
    return slidesPerViewDesktop;
  };

  const sliderHeight = () => {
    const breakpoint = useBreakpoint();
    if (breakpoint === 'sm' || breakpoint === 'xs') {
      return '156px';
    }
    return '232px';
  };

  const calculateCurrentSlideIdx = useCallback(
    (index) => {
      const maxIndex = 8;

      const calculatedIndex = index + currentSlideIdxOffset;

      if (!slides || slides.length === 0) {
        return 0;
      }

      if (calculatedIndex > slides.length - 1) {
        return calculatedIndex - maxIndex - 1;
      }

      return calculatedIndex;
    },
    [slides]
  );

  useEffect(() => {
    if (slider && currentSlideIdx !== null && slides.length > 0) {
      try {
        setTimeout(() => {
          slider.moveToIdx(currentSlideIdx);
        }, 0);
      } catch (error) {
        rollbar.error('Failed to move slider:', error);
      }
    }
  }, [slider, currentSlideIdx, slides]);

  useEffect(() => {
    const filteredSubcategories = subcategoryNavigation?.sibling_categories
      ?.map(({name, path}) => {
        const visualFilterProperties = visualFilterMapping[name];
        if (!visualFilterProperties) {
          return null;
        }
        const {title, imageUrl} = visualFilterProperties;
        return {name, path, title, imageUrl};
      })
      .filter(
        (item) => item && item.name && item.path && item.title && item.imageUrl
      );
    setFilteredSubcategories(filteredSubcategories);
  }, [subcategoryNavigation]);

  useEffect(() => {
    const slides =
      filteredSubcategories?.map((item) => {
        return (
          <VisualSubcategoryLink
            key={item.name}
            imageUrl={`${imagePath}${item.imageUrl}`}
            title={item.title}
            linkUrl={item.path}
            active={item.name === categoryName}
            small={isMobileView}
            onClick={() => fireMetricsEvent(parentCategoryName, item.name)}
          />
        );
      }) || [];

    setSlides(slides);
  }, [filteredSubcategories, categoryName, imagePath]);

  useEffect(() => {
    const currentCategoryIndex = filteredSubcategories?.findIndex(
      (category) => category.name === categoryName
    );

    const newCurrentSlideIdx = calculateCurrentSlideIdx(currentCategoryIndex);
    setCurrentSlideIdx(newCurrentSlideIdx);
  }, [slides, categoryName, calculateCurrentSlideIdx, subcategoryNavigation]);

  return (
    <Carousel
      arrows={isMobileView ? false : true}
      onCreated={onCreated}
      dots={false}
      slides={slides}
      slidesPerView={slidesPerView()}
      sliderHeight={sliderHeight()}
      slidesSpacing={16}
      sx={{
        margin: 'auto',
        paddingTop: `${isMobileView ? '0.5rem' : '2rem'}`,
        paddingBottom: `${isMobileView ? '0.5rem' : '1.5rem'}`,
        '.keen-slider': {
          display: 'flex',
          overflow: 'hidden',
          height: `${sliderHeight()} !important`
        },
        '.keen-slider__slide': {
          flexShrink: 0,
          '& span': {
            height: 'auto !important'
          },
          '& img': {
            aspectRatio: '1 / 1'
          }
        }
      }}
    />
  );
};

VisualFilters.propTypes = {
  subcategoryNavigation: PropTypes.shape({
    sibling_categories: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired
      })
    )
  }),
  categoryName: PropTypes.string
};

VisualFilters.defaultProps = {
  subcategoryNavigation: null,
  categoryName: ''
};

export default VisualFilters;
